// The signed-in device's chats: the inbox and the threads opened so far,
// kept current by one chat stream and repaired over REST when the stream
// can't be resumed. React reads it through `useChat` (ChatProvider).
import type { ApiClient, Schemas } from "@10twenty/api";
import { ApiError, errorMessage, unwrap } from "../api";
import type { SessionManager } from "../auth/session";
import { streamUrl, type SocketLike, type StreamStatus } from "../live/circleStream";
import {
  checkBody,
  emptyThread,
  mergeInbox,
  newClientId,
  newestSeq,
  pruneTyping,
  removeConversation,
  TYPING_SEND_MS,
  TYPING_SHOW_MS,
  upsertConversation,
  withMessage,
  withMessages,
  withNewestPage,
  withRead,
  withoutTyping,
  withReadPosition,
  resignCovered,
  withResigned,
  withTyping,
  withUsers,
  type ChatFrame,
  type ChatUser,
  type Conversation,
  type Message,
  type PendingSend,
  type Thread,
  type Typing,
  type Users,
} from "./chat";
import { ChatStream } from "./chatStream";
import { jumpStep } from "./search";
import type { Media } from "./media";
import { locationErrorText, type LocationCreate } from "./location";
import type { VoiceClip } from "./voice";

const PAGE = 50;
/** Inbox pages fetched at most (the inbox is every circle chat plus DMs). */
const MAX_INBOX_PAGES = 20;
const RETRY_MS = 5_000;
/** Jumping to a search result pages back in pages this large, at most this many. */
const JUMP_PAGE = 100;
const MAX_JUMP_PAGES = 200;

export interface ThreadState extends Thread {
  /** Current participants (from `GET /conversations/{id}`), once loaded. */
  participants: ChatUser[] | null;
  loadingOlder: boolean;
  error: string | null;
  /** The caller can no longer read it (left, removed, deleted). */
  lost: boolean;
}

export interface ChatSnapshot {
  status: StreamStatus;
  /** The inbox has loaded at least once. */
  inboxLoaded: boolean;
  inboxError: string | null;
  /** Every known conversation, newest activity first (DMs without messages included; see `isListed`). */
  conversations: Conversation[];
  users: Users;
  threads: Readonly<Record<string, ThreadState>>;
  /** Who is typing where (from `chat_typing`, expiring after `TYPING_SHOW_MS`). */
  typing: Typing;
}

export type ConversationSettings = Schemas["ConversationSettingsUpdate"];

export interface ChatStoreDeps {
  selfId: string;
  deviceId: string;
  api: ApiClient;
  session: SessionManager;
  connect?: (url: string) => SocketLike;
  newClientId?: () => string;
}

function newThread(): ThreadState {
  return { ...emptyThread(), participants: null, loadingOlder: false, error: null, lost: false };
}

function isGone(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

export class ChatStore {
  private snapshot: ChatSnapshot = {
    status: "connecting",
    inboxLoaded: false,
    inboxError: null,
    conversations: [],
    users: {},
    threads: {},
    typing: {},
  };
  private readonly listeners = new Set<() => void>();
  /** `changed()` waits, resolved on the next change or on stop. */
  private readonly waiters = new Set<() => void>();
  private stream: ChatStream | null = null;
  private cursor: string | null = null;
  private stopped = false;
  /** Inbox refresh state: one at a time, another queued when asked meanwhile. */
  private inboxLoading = false;
  private inboxAgain = false;
  /** Conversations frames changed or removed during the current inbox fetch. */
  private touched = new Set<string>();
  private removed = new Set<string>();
  /**
   * Bumped by every frame (and local change) to a conversation. A REST
   * response about a conversation applies only if no frame came in while it
   * was in flight; otherwise the conversation is fetched again shortly.
   */
  private readonly revisions = new Map<string, number>();
  /** The latest newest-page request per thread; older responses are dropped. */
  private readonly newestRequests = new Map<string, number>();
  /** Bumped when a thread resets; older-page responses from before it are dropped. */
  private readonly epochs = new Map<string, number>();
  /** Message frames applied to a thread while its reset page is in flight. */
  private readonly arrived = new Map<string, Message[]>();
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  /** Conversations fetched because a frame named one not in the inbox. */
  private readonly fetching = new Set<string>();
  /** Each thread's older-page request under way. */
  private readonly olderLoads = new Map<string, Promise<void>>();
  private readonly refreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly reading = new Map<string, number>();
  /** When this device last said it was typing, per conversation. */
  private readonly typingSent = new Map<string, number>();
  private typingTimer: ReturnType<typeof setTimeout> | undefined;
  /** Messages whose media URLs expired, fetched again in one batch per thread. */
  private readonly resigning = new Map<string, Set<number>>();

  constructor(private readonly deps: ChatStoreDeps) {}

  // ------------------------------------------------------------ lifecycle

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): ChatSnapshot => this.snapshot;

  /** Connects and loads the inbox; after `stop` it starts over (React may remount). */
  start(): void {
    this.stopped = false;
    const deps = this.deps;
    this.stream = new ChatStream({
      url: streamUrl(window.location.origin),
      deviceId: deps.deviceId,
      connect: deps.connect ?? ((url) => new WebSocket(url)),
      session: () => deps.session.activeSession(),
      refresh: (stale) => deps.session.refresh(stale),
      cursor: () => this.cursor,
      onStatus: (status) => this.update({ status }),
      onFrame: (frame) => this.onFrame(frame),
    });
    this.stream.start();
    void this.loadInbox();
  }

  stop(): void {
    this.stopped = true;
    this.stream?.stop();
    this.stream = null;
    clearTimeout(this.retryTimer);
    clearTimeout(this.typingTimer);
    for (const timer of this.refreshTimers.values()) clearTimeout(timer);
    this.refreshTimers.clear();
    this.wake();
  }

  private wake(): void {
    const waiters = [...this.waiters];
    this.waiters.clear();
    for (const waiter of waiters) waiter();
  }

  /** Reconnects now if the stream is waiting to retry. */
  nudge(): void {
    this.stream?.nudge();
  }

  private update(patch: Partial<ChatSnapshot>): void {
    if (this.stopped) return;
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
    this.wake();
  }

  private setThread(id: string, change: (thread: ThreadState) => ThreadState): void {
    const prev = this.snapshot.threads[id];
    if (!prev) return;
    const next = change(prev);
    if (next !== prev) this.update({ threads: { ...this.snapshot.threads, [id]: next } });
  }

  private addUsers(list: readonly ChatUser[]): void {
    const users = withUsers(this.snapshot.users, list);
    if (users !== this.snapshot.users) this.update({ users });
  }

  private revision(id: string): number {
    return this.revisions.get(id) ?? 0;
  }

  /** A frame (or this device's own write) changed the conversation: REST responses in flight are stale. */
  private markChanged(id: string): void {
    this.revisions.set(id, this.revision(id) + 1);
    this.touched.add(id);
  }

  /** A change from a frame (or this device's own write): newer than any REST response in flight. */
  private putConversation(conversation: Conversation): void {
    this.markChanged(conversation.id);
    this.removed.delete(conversation.id);
    this.update({ conversations: upsertConversation(this.snapshot.conversations, conversation) });
  }

  /** A REST response requested at `revision`: applied only if no frame changed the conversation since. */
  private putFetched(conversation: Conversation, revision: number): void {
    if (this.revision(conversation.id) !== revision) {
      this.refreshSoon(conversation.id);
      return;
    }
    this.removed.delete(conversation.id);
    this.update({ conversations: upsertConversation(this.snapshot.conversations, conversation) });
  }

  private conversation(id: string): Conversation | undefined {
    return this.snapshot.conversations.find((c) => c.id === id);
  }

  // ---------------------------------------------------------------- inbox

  /** Fetches the whole inbox and merges it into what frames have applied meanwhile. */
  private async loadInbox(): Promise<void> {
    if (this.inboxLoading) {
      this.inboxAgain = true;
      return;
    }
    this.inboxLoading = true;
    clearTimeout(this.retryTimer);
    this.touched = new Set();
    this.removed = new Set();
    try {
      const fresh: Conversation[] = [];
      let pageToken: string | undefined;
      for (let i = 0; i < MAX_INBOX_PAGES; i++) {
        const page = unwrap(
          await this.deps.api.GET("/me/conversations", {
            params: { query: { limit: 100, ...(pageToken ? { page_token: pageToken } : {}) } },
          }),
        );
        if (this.stopped) return;
        fresh.push(...page.items);
        this.addUsers(page.users);
        if (!page.next_page_token) break;
        pageToken = page.next_page_token;
      }
      this.update({
        conversations: mergeInbox(this.snapshot.conversations, fresh, this.touched, this.removed),
        inboxLoaded: true,
        inboxError: null,
      });
      // Frames kept the cached copies of these; the fetch may know more (counts, settings).
      for (const c of fresh) if (this.touched.has(c.id)) this.refreshSoon(c.id);
    } catch (error) {
      if (this.stopped) return;
      this.update({ inboxError: errorMessage(error) });
      this.retryTimer = setTimeout(() => void this.loadInbox(), RETRY_MS);
    } finally {
      this.inboxLoading = false;
      if (this.inboxAgain && !this.stopped) {
        this.inboxAgain = false;
        void this.loadInbox();
      }
    }
  }

  /** Refetches one conversation (a frame named one not in the inbox, or counts may be off). */
  private async fetchConversation(id: string): Promise<void> {
    if (this.fetching.has(id)) return;
    this.fetching.add(id);
    const revision = this.revision(id);
    try {
      const detail = unwrap(
        await this.deps.api.GET("/conversations/{conversationId}", { params: { path: { conversationId: id } } }),
      );
      if (this.stopped) return;
      this.addUsers(detail.users);
      this.putFetched(detail.conversation, revision);
      this.setThread(id, (thread) => ({ ...thread, participants: detail.participants }));
    } catch (error) {
      if (!isGone(error)) return;
      if (this.revision(id) === revision) this.forget(id);
      else this.refreshSoon(id);
    } finally {
      this.fetching.delete(id);
    }
  }

  private refreshSoon(id: string): void {
    if (this.refreshTimers.has(id)) return;
    this.refreshTimers.set(
      id,
      setTimeout(() => {
        this.refreshTimers.delete(id);
        void this.fetchConversation(id);
      }, 1_000),
    );
  }

  private forget(id: string): void {
    this.revisions.set(id, this.revision(id) + 1);
    this.removed.add(id);
    this.touched.delete(id);
    this.update({ conversations: removeConversation(this.snapshot.conversations, id) });
    this.setThread(id, (thread) => ({ ...thread, lost: true }));
  }

  /** Gets or creates the DM with `userId` (403 `not_connected` unless they share a circle). */
  async direct(userId: string): Promise<string> {
    const detail = unwrap(await this.deps.api.POST("/me/conversations/direct", { body: { user_id: userId } }));
    this.addUsers(detail.users);
    this.putConversation(detail.conversation);
    return detail.conversation.id;
  }

  // -------------------------------------------------------------- frames

  private onFrame(frame: ChatFrame): void {
    if (this.stopped) return;
    switch (frame.type) {
      case "chat_subscribed":
        this.cursor = frame.cursor;
        if (!frame.resumed) this.repair();
        return;
      case "chat_resync":
        this.cursor = frame.cursor;
        this.repair();
        return;
      case "chat_message": {
        const message = frame.message;
        const id = message.conversation_id;
        this.addUsers(frame.users);
        const conversation = this.conversation(id);
        if (conversation) {
          this.putConversation(withMessage(conversation, message, this.deps.selfId));
          // The local count is a guess (a `chat_read` built later may already include this
          // message, and unsending takes back an amount only the server knows): confirm it.
          if (message.sender_id !== this.deps.selfId && (frame.change === "new" || frame.change === "unsent")) {
            this.refreshSoon(id);
          }
        } else {
          this.markChanged(id);
          void this.fetchConversation(id);
        }
        this.arrived.get(id)?.push(message);
        this.setThread(id, (thread) => withMessages(thread, [message]));
        if (frame.change === "new" && message.sender_id) this.stopTyping(id, message.sender_id);
        break;
      }
      case "chat_conversation":
        this.addUsers(frame.users);
        if (frame.change === "removed" || frame.conversation === null) {
          this.forget(frame.conversation_id);
        } else {
          this.putConversation(frame.conversation);
          const thread = this.snapshot.threads[frame.conversation_id];
          if (thread?.lost) {
            // Rejoined: the thread starts again from the new membership.
            this.update({ threads: { ...this.snapshot.threads, [frame.conversation_id]: newThread() } });
            this.reloadThread(frame.conversation_id);
          } else if (frame.change === "updated" && thread) {
            // Cleared on another device, or renamed: reload what the thread shows.
            void this.loadNewest(frame.conversation_id, true);
          }
        }
        break;
      case "chat_read": {
        const conversation = this.conversation(frame.conversation_id);
        if (conversation) this.putConversation(withRead(conversation, frame));
        break;
      }
      case "chat_receipt":
        this.setThread(frame.conversation_id, (thread) => withReadPosition(thread, frame.user_id, frame.read_seq));
        return;
      case "chat_typing":
        if (frame.user_id === this.deps.selfId) return;
        this.update({
          typing: withTyping(this.snapshot.typing, frame.conversation_id, frame.user_id, Date.now() + TYPING_SHOW_MS),
        });
        this.scheduleTypingPrune();
        return;
    }
    this.cursor = frame.cursor;
  }

  /**
   * The stream could not be resumed (or has just started): refetch the inbox
   * and reload every thread from its newest page, also those still loading
   * (their first page may predate the subscription). A reset rather than a
   * merge, since messages cleared on another device must go.
   */
  private repair(): void {
    void this.loadInbox();
    for (const [id, thread] of Object.entries(this.snapshot.threads)) {
      if (!thread.lost) this.reloadThread(id);
    }
  }

  private reloadThread(id: string): void {
    void this.loadDetail(id);
    void this.loadNewest(id, true);
  }

  // -------------------------------------------------------------- threads

  /** Starts loading a thread the first time it is shown. */
  open(id: string): void {
    const existing = this.snapshot.threads[id];
    if (existing && !existing.error) return;
    if (!existing) this.update({ threads: { ...this.snapshot.threads, [id]: newThread() } });
    else this.setThread(id, (thread) => ({ ...thread, error: null }));
    if (existing?.loaded) {
      this.reloadThread(id);
    } else {
      void this.loadDetail(id);
      void this.loadNewest(id, false);
    }
  }

  private async loadDetail(id: string): Promise<void> {
    const revision = this.revision(id);
    try {
      const detail = unwrap(
        await this.deps.api.GET("/conversations/{conversationId}", { params: { path: { conversationId: id } } }),
      );
      if (this.stopped) return;
      this.addUsers(detail.users);
      this.putFetched(detail.conversation, revision);
      this.setThread(id, (thread) => {
        let next: ThreadState = { ...thread, participants: detail.participants };
        for (const p of detail.read_positions) next = withReadPosition(next, p.user_id, p.read_seq);
        return next;
      });
    } catch (error) {
      this.failThread(id, error);
    }
  }

  /** Resolves to whether this page was applied (not superseded, failed or stopped). */
  private async loadNewest(id: string, reset: boolean): Promise<boolean> {
    const request = (this.newestRequests.get(id) ?? 0) + 1;
    this.newestRequests.set(id, request);
    const arrived: Message[] = [];
    if (reset) {
      this.epochs.set(id, (this.epochs.get(id) ?? 0) + 1);
      this.arrived.set(id, arrived);
      this.setThread(id, (thread) => (thread.loadingOlder ? { ...thread, loadingOlder: false } : thread));
    }
    try {
      const page = unwrap(
        await this.deps.api.GET("/conversations/{conversationId}/messages", {
          params: { path: { conversationId: id }, query: { limit: PAGE } },
        }),
      );
      if (this.stopped || this.newestRequests.get(id) !== request) return false;
      this.addUsers(page.users);
      this.setThread(id, (thread) => withNewestPage(thread, page.items, page.has_more, reset, arrived));
      return true;
    } catch (error) {
      if (this.newestRequests.get(id) === request) this.failThread(id, error);
      return false;
    } finally {
      if (this.arrived.get(id) === arrived) this.arrived.delete(id);
    }
  }

  /** Pages in the messages before the oldest loaded (one request at a time per thread). */
  loadOlder(id: string, limit = PAGE): Promise<void> {
    const inFlight = this.olderLoads.get(id);
    if (inFlight) return inFlight;
    const thread = this.snapshot.threads[id];
    const first = thread?.messages[0];
    if (!thread || !first || !thread.hasOlder || thread.loadingOlder) return Promise.resolve();
    const load: Promise<void> = this.fetchOlder(id, first.seq, limit).finally(() => {
      if (this.olderLoads.get(id) === load) this.olderLoads.delete(id);
    });
    this.olderLoads.set(id, load);
    return load;
  }

  private async fetchOlder(id: string, before: number, limit: number): Promise<void> {
    const epoch = this.epochs.get(id) ?? 0;
    this.setThread(id, (t) => ({ ...t, loadingOlder: true }));
    try {
      const page = unwrap(
        await this.deps.api.GET("/conversations/{conversationId}/messages", {
          params: { path: { conversationId: id }, query: { before, limit } },
        }),
      );
      // A reset since (say, the chat was cleared) makes this page stale.
      if (this.stopped || (this.epochs.get(id) ?? 0) !== epoch) return;
      this.addUsers(page.users);
      this.setThread(id, (t) => ({ ...withMessages(t, page.items, page.has_more), loadingOlder: false }));
    } catch (error) {
      if ((this.epochs.get(id) ?? 0) !== epoch) return;
      this.setThread(id, (t) => ({ ...t, loadingOlder: false }));
      this.failThread(id, error);
    }
  }

  /**
   * Loads the thread back to message `seq` (a search result), resolving to
   * whether it is now loaded: false when it is not there to show (unsent,
   * cleared, no longer readable) or loading failed. Open the thread first.
   */
  async loadThrough(id: string, seq: number): Promise<boolean> {
    let pages = 0;
    let reloaded = false;
    while (pages < MAX_JUMP_PAGES && !this.stopped) {
      const thread = this.snapshot.threads[id];
      if (!thread || thread.lost || thread.error) return false;
      const step = jumpStep(thread, seq);
      if (step === "shown") return true;
      if (step === "missing") {
        // Newer than the cached run (say, sent while the stream was down): reload from the newest page once.
        if (reloaded || seq <= newestSeq(thread)) return false;
        // Superseded (say, by a reconnect's repair): ask again rather than judge a stale run.
        reloaded = await this.loadNewest(id, true);
        pages++;
      } else if (step === "wait") {
        await this.changed();
      } else {
        await this.loadOlder(id, JUMP_PAGE);
        pages++;
      }
    }
    return false;
  }

  /** Resolves on the next snapshot change (or once stopped). */
  private changed(): Promise<void> {
    return new Promise((resolve) => {
      if (this.stopped) return resolve();
      this.waiters.add(resolve);
    });
  }

  private failThread(id: string, error: unknown): void {
    if (this.stopped) return;
    if (isGone(error)) this.forget(id);
    else this.setThread(id, (thread) => ({ ...thread, error: errorMessage(error) }));
  }

  /** Marks the conversation read up to `seq` (call only while its newest messages are on screen). */
  markRead(id: string, seq: number): void {
    const conversation = this.conversation(id);
    if (!conversation || seq <= conversation.read_seq || (this.reading.get(id) ?? 0) >= seq) return;
    this.reading.set(id, seq);
    const revision = this.revision(id);
    void this.deps.api
      .POST("/conversations/{conversationId}/read", { params: { path: { conversationId: id } }, body: { seq } })
      .then(({ data }) => {
        if (data && !this.stopped) this.putFetched(data, revision);
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.reading.get(id) === seq) this.reading.delete(id);
      });
  }

  // ---------------------------------------------------------------- sends

  /**
   * Sends text, or uploaded `attachments` with an optional caption,
   * optimistically; it shows as pending until the server has it. Returns
   * false if the body is invalid.
   */
  send(
    id: string,
    text: string,
    options: { replyToId?: string | null; mentions?: string[]; attachments?: Media[] } = {},
  ): boolean {
    const { body, ok } = checkBody(text);
    const attachments = options.attachments ?? [];
    if (!(ok || (attachments.length > 0 && body === "")) || !this.snapshot.threads[id]) return false;
    const pending: PendingSend = {
      client_id: this.deps.newClientId?.() ?? newClientId(),
      body,
      ...(attachments.length ? { attachments } : {}),
      created_at: new Date().toISOString(),
      status: "sending",
      error: null,
      code: null,
      ...(options.replyToId ? { reply_to_id: options.replyToId } : {}),
      ...(options.mentions?.length ? { mentions: options.mentions } : {}),
    };
    this.typingSent.delete(id);
    this.setThread(id, (thread) => ({ ...thread, pending: [...thread.pending, pending] }));
    void this.post(id, pending);
    return true;
  }

  /**
   * Sends a location message (a pin, a meeting point or a live ETA)
   * optimistically, and pins it once the server has it when `pin` is set.
   */
  sendLocation(id: string, location: LocationCreate, options: { replyToId?: string | null; pin?: boolean } = {}): void {
    if (!this.snapshot.threads[id]) return;
    const pending: PendingSend = {
      client_id: this.deps.newClientId?.() ?? newClientId(),
      body: "",
      location,
      ...(options.pin ? { pin: true } : {}),
      created_at: new Date().toISOString(),
      status: "sending",
      error: null,
      code: null,
      ...(options.replyToId ? { reply_to_id: options.replyToId } : {}),
    };
    this.setThread(id, (thread) => ({ ...thread, pending: [...thread.pending, pending] }));
    void this.post(id, pending);
  }

  /** Sends an uploaded voice clip with its waveform optimistically. */
  sendVoice(id: string, media: Media, voice: VoiceClip, options: { replyToId?: string | null } = {}): void {
    if (!this.snapshot.threads[id]) return;
    const pending: PendingSend = {
      client_id: this.deps.newClientId?.() ?? newClientId(),
      body: "",
      attachments: [media],
      voice,
      created_at: new Date().toISOString(),
      status: "sending",
      error: null,
      code: null,
      ...(options.replyToId ? { reply_to_id: options.replyToId } : {}),
    };
    this.setThread(id, (thread) => ({ ...thread, pending: [...thread.pending, pending] }));
    void this.post(id, pending);
  }

  /** Sends a failed message again with the same `client_id`, so it is never posted twice. */
  retry(id: string, clientId: string): void {
    const pending = this.snapshot.threads[id]?.pending.find((p) => p.client_id === clientId);
    if (!pending || pending.status !== "failed") return;
    const again: PendingSend = { ...pending, status: "sending", error: null, code: null };
    this.setThread(id, (thread) => ({
      ...thread,
      pending: thread.pending.map((p) => (p.client_id === clientId ? again : p)),
    }));
    void this.post(id, again);
  }

  discard(id: string, clientId: string): void {
    this.setThread(id, (thread) => ({ ...thread, pending: thread.pending.filter((p) => p.client_id !== clientId) }));
  }

  private async post(id: string, pending: PendingSend): Promise<void> {
    try {
      const message = unwrap(
        await this.deps.api.POST("/conversations/{conversationId}/messages", {
          params: { path: { conversationId: id } },
          body: {
            client_id: pending.client_id,
            ...(pending.location
              ? { kind: "location", location: pending.location }
              : pending.voice && pending.attachments?.length
                ? {
                    kind: "voice",
                    attachment_ids: pending.attachments.map((a) => a.id),
                    voice: { waveform: pending.voice.waveform },
                  }
                : pending.attachments?.length
                  ? { kind: "media", attachment_ids: pending.attachments.map((a) => a.id) }
                  : { kind: "text" }),
            ...(pending.body ? { body: pending.body } : {}),
            ...(pending.reply_to_id ? { reply_to_id: pending.reply_to_id } : {}),
            ...(pending.mentions?.length ? { mentions: pending.mentions } : {}),
          },
        }),
      );
      if (this.stopped) return;
      this.applyMessage(id, message);
      // Pinning is a second request: a refusal leaves the message sent, unpinned.
      if (pending.pin) await this.pin(id, message.id).catch(() => undefined);
    } catch (error) {
      if (this.stopped) return;
      const code = error instanceof ApiError ? error.code : undefined;
      const failed: PendingSend = {
        ...pending,
        status: "failed",
        error: errorMessage(error, {
          403: code === "not_writable" ? "You can't send messages here." : (locationErrorText(code) ?? ""),
        }),
        code: error instanceof ApiError ? (error.code ?? null) : null,
      };
      this.setThread(id, (thread) => ({
        ...thread,
        pending: thread.pending.map((p) => (p.client_id === pending.client_id ? failed : p)),
      }));
    }
  }

  /** A message this device changed, as the server returned it. */
  private applyMessage(id: string, message: Message): void {
    // Like a frame: a reset page still in flight must not undo it.
    this.arrived.get(id)?.push(message);
    const conversation = this.conversation(id);
    if (conversation) this.putConversation(withMessage(conversation, message, this.deps.selfId));
    this.setThread(id, (thread) => withMessages(thread, [message]));
  }

  /**
   * Fetches messages again for freshly signed media URLs (they last about an
   * hour); calls within a moment are batched into one page.
   */
  resign(id: string, seq: number): void {
    const queued = this.resigning.get(id);
    if (queued) {
      queued.add(seq);
      return;
    }
    this.resigning.set(id, new Set([seq]));
    setTimeout(() => void this.fetchResigned(id), 200);
  }

  private async fetchResigned(id: string): Promise<void> {
    // The live queue: seqs asked for while a page is in flight are fetched next.
    const queue = this.resigning.get(id);
    try {
      while (queue && queue.size > 0) {
        // One page from the newest asked for back, as far as a page reaches.
        const newest = Math.max(...queue);
        const page = unwrap(
          await this.deps.api.GET("/conversations/{conversationId}/messages", {
            params: { path: { conversationId: id }, query: { before: newest + 1, limit: PAGE } },
          }),
        );
        if (this.stopped) return;
        this.setThread(id, (thread) => withResigned(thread, page.items));
        for (const seq of resignCovered(queue, newest, page)) queue.delete(seq);
      }
    } catch {
      // The images stay broken until shown again.
    } finally {
      this.resigning.delete(id);
    }
  }

  // -------------------------------------------------------------- actions
  // These throw the server's error for the page to show.

  /** Edits your own text message (within 15 minutes of sending). */
  async edit(id: string, messageId: string, text: string, mentions: string[]): Promise<void> {
    const { body, ok } = checkBody(text);
    if (!ok) throw new Error("A message needs 1 to 4,000 characters.");
    const message = unwrap(
      await this.deps.api.PATCH("/conversations/{conversationId}/messages/{messageId}", {
        params: { path: { conversationId: id, messageId } },
        body: { body, mentions },
      }),
    );
    if (!this.stopped) this.applyMessage(id, message);
  }

  /** Unsends your own message, or (as a circle admin) removes someone else's. */
  async unsend(id: string, messageId: string): Promise<void> {
    const message = unwrap(
      await this.deps.api.DELETE("/conversations/{conversationId}/messages/{messageId}", {
        params: { path: { conversationId: id, messageId } },
      }),
    );
    if (!this.stopped) this.applyMessage(id, message);
  }

  /** Sets your reaction (one per message), or takes it back with null. */
  async react(id: string, messageId: string, emoji: string | null): Promise<void> {
    const params = { path: { conversationId: id, messageId } };
    const message = unwrap(
      emoji === null
        ? await this.deps.api.DELETE("/conversations/{conversationId}/messages/{messageId}/reaction", { params })
        : await this.deps.api.PUT("/conversations/{conversationId}/messages/{messageId}/reaction", {
            params,
            body: { emoji },
          }),
    );
    if (!this.stopped) this.applyMessage(id, message);
  }

  /** Pins a message for everyone in the conversation (replacing the pinned one). */
  async pin(id: string, messageId: string): Promise<void> {
    const revision = this.revision(id);
    const conversation = unwrap(
      await this.deps.api.PUT("/conversations/{conversationId}/pin", {
        params: { path: { conversationId: id } },
        body: { message_id: messageId },
      }),
    );
    if (!this.stopped) this.putOwnWrite(conversation, revision);
  }

  /** Unpins the conversation's pinned message. */
  async unpin(id: string): Promise<void> {
    const revision = this.revision(id);
    const conversation = unwrap(
      await this.deps.api.DELETE("/conversations/{conversationId}/pin", { params: { path: { conversationId: id } } }),
    );
    if (!this.stopped) this.putOwnWrite(conversation, revision);
  }

  /** Sets the conversation's disappearing-message timer for messages sent from now on (null: off). */
  async setTimer(id: string, seconds: number | null): Promise<void> {
    const revision = this.revision(id);
    const conversation = unwrap(
      await this.deps.api.PUT("/conversations/{conversationId}/timer", {
        params: { path: { conversationId: id } },
        body: { message_timer: seconds },
      }),
    );
    if (!this.stopped) this.putOwnWrite(conversation, revision);
  }

  /** Stops your own live ETA. */
  async stopEta(id: string, messageId: string): Promise<void> {
    const message = unwrap(
      await this.deps.api.POST("/conversations/{conversationId}/messages/{messageId}/stop", {
        params: { path: { conversationId: id, messageId } },
      }),
    );
    if (!this.stopped) this.applyMessage(id, message);
  }

  /** Changes this user's notify level or mute for the conversation. */
  async updateSettings(id: string, settings: ConversationSettings): Promise<void> {
    const revision = this.revision(id);
    const conversation = unwrap(
      await this.deps.api.PATCH("/conversations/{conversationId}/settings", {
        params: { path: { conversationId: id } },
        body: settings,
      }),
    );
    if (!this.stopped) this.putOwnWrite(conversation, revision);
  }

  /** Hides everything so far from this user (on every device); others keep their history. */
  async clear(id: string): Promise<void> {
    const revision = this.revision(id);
    const conversation = unwrap(
      await this.deps.api.POST("/conversations/{conversationId}/clear", { params: { path: { conversationId: id } } }),
    );
    if (this.stopped) return;
    this.putOwnWrite(conversation, revision);
    if (this.snapshot.threads[id]) void this.loadNewest(id, true);
  }

  /**
   * The conversation as this device's write returned it: newer than any REST
   * response still in flight (which the revision bump makes stale), unless a
   * frame changed it meanwhile, in which case it is fetched again.
   */
  private putOwnWrite(conversation: Conversation, revision: number): void {
    if (this.revision(conversation.id) === revision) this.putConversation(conversation);
    else this.refreshSoon(conversation.id);
  }

  /** Tells the others this user is typing (call on every keystroke; sent at most every 3 s). */
  typing(id: string): void {
    const now = Date.now();
    if (now - (this.typingSent.get(id) ?? -Infinity) < TYPING_SEND_MS) return;
    if (this.stream?.typing(id)) this.typingSent.set(id, now);
  }

  private stopTyping(id: string, userId: string): void {
    const typing = withoutTyping(this.snapshot.typing, id, userId);
    if (typing !== this.snapshot.typing) this.update({ typing });
  }

  /** Clears expired typing entries when the earliest one ends. */
  private scheduleTypingPrune(): void {
    clearTimeout(this.typingTimer);
    const ends = Object.values(this.snapshot.typing).flatMap((people) => Object.values(people));
    if (ends.length === 0) return;
    this.typingTimer = setTimeout(
      () => {
        const typing = pruneTyping(this.snapshot.typing, Date.now());
        if (typing !== this.snapshot.typing) this.update({ typing });
        this.scheduleTypingPrune();
      },
      Math.max(0, Math.min(...ends) - Date.now()) + 50,
    );
  }
}
